import { useState, useEffect, useCallback } from 'react';
import { activityAPI, fetchAllPages, type UserActivityLog, type ActivityStats } from '../lib/api';
import { useView } from '../contexts/ViewContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  History,
  Search,
  Activity as ActivityIcon,
  TrendingUp,
  Users,
  BarChart3,
  AlertCircle,
  Loader2,
  RefreshCw,
  Calendar,
  User,
  FileText,
  Download,
} from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { usePagination } from '../hooks';
import { getErrorMessage } from '../hooks/useFetchData';
import { toast } from 'sonner';

// Action badge color configuration
const ACTION_BADGE_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  delete: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  view: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  login: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  approve: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  reject: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export default function UserActivity() {
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [activeTab, setActiveTab] = useState('activities');
  const [activities, setActivities] = useState<UserActivityLog[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  const pagination = usePagination({ pageSize: 50 });

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const viewParams = getViewQueryParams();
      const params: Record<string, string | number | undefined> = {
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        ...viewParams,
      };

      if (actionFilter && actionFilter !== 'all') {
        params['action_type'] = actionFilter;
      }

      if (entityFilter && entityFilter !== 'all') {
        params['entity'] = entityFilter;
      }

      if (searchTerm) {
        params['search'] = searchTerm;
      }

      const response = await activityAPI.getActivityLogs(params);
      setActivities(response.data.results || []);
      pagination.setFromResponse(response.data.count || 0, (response.data.results || []).length);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load activity logs'));
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, actionFilter, entityFilter, searchTerm, getViewQueryParams]);

  const loadStats = useCallback(async () => {
    try {
      const response = await activityAPI.getActivityStats();
      setStats(response.data);
    } catch (err: unknown) {
      console.error('Error loading stats:', err);
    }
  }, []);

  const handleExportToExcel = async () => {
    try {
      // Fetch ALL activities for export — the backend clamps page_size, so
      // the old single page_size=10000 request silently exported one page.
      const viewParams = getViewQueryParams();
      const params: Record<string, string | number | undefined> = {
        page_size: 1000,
        ...viewParams,
      };

      if (actionFilter && actionFilter !== 'all') {
        params['action_type'] = actionFilter;
      }

      if (entityFilter && entityFilter !== 'all') {
        params['entity'] = entityFilter;
      }

      if (searchTerm) {
        params['search'] = searchTerm;
      }

      toast.info('Preparing export...');
      const allActivities = await fetchAllPages<UserActivityLog>(
        (page) => activityAPI.getActivityLogs({ ...params, page }),
      );

      if (allActivities.length === 0) {
        toast.error('No activities to export');
        return;
      }

      // Transform data for Excel
      const exportData = allActivities.map((activity) => ({
        'User Name': activity.user_name,
        'Action': activity.action,
        'Entity Type': activity.entity_type_display,
        'Entity Name': activity.entity_name || '',
        'Description': activity.description,
        'Timestamp': format(new Date(activity.timestamp), 'yyyy-MM-dd HH:mm:ss'),
        'IP Address': activity.ip_address || '',
      }));

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Activity Logs');

      // Auto-size columns
      const colWidths = [
        { wch: 20 }, // User Name
        { wch: 30 }, // User Email
        { wch: 12 }, // Action
        { wch: 18 }, // Entity Type
        { wch: 25 }, // Entity Name
        { wch: 50 }, // Description
        { wch: 20 }, // Timestamp
        { wch: 15 }, // IP Address
      ];
      worksheet['!cols'] = colWidths;

      // Generate filename with date
      const filename = `user_activity_history_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);
      toast.success(`Exported ${allActivities.length} activities to Excel`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      toast.error('Failed to export activities');
    }
  };

  useEffect(() => {
    if (viewContextLoading) return;
    loadActivities();
    loadStats();
  }, [viewContextLoading, loadActivities, loadStats, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  const handleSearch = () => {
    pagination.reset();
    loadActivities();
  };

  const getActionBadge = (action: string) => {
    return (
      <Badge className={ACTION_BADGE_COLORS[action] || 'bg-gray-100 text-gray-800'}>
        {action}
      </Badge>
    );
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading activity history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <History className="h-8 w-8" />
            User Activity History
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and monitor all user actions and system events
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportToExcel}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
          <Button variant="outline" onClick={() => { loadActivities(); loadStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Activities</CardTitle>
              <ActivityIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_activities.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {stats.activities_today} today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Week</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activities_this_week.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Last 7 days
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.top_users.length}</div>
              <p className="text-xs text-muted-foreground">
                Most active this period
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="activities">Activity Logs</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
        </TabsList>

        {/* Activity Logs Tab */}
        <TabsContent value="activities" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search activities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="view">View</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="approve">Approve</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={entityFilter} onValueChange={setEntityFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Entities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entities</SelectItem>
                    <SelectItem value="purchase_order">Purchase Orders</SelectItem>
                    <SelectItem value="ncr">NCRs</SelectItem>
                    <SelectItem value="supplier">Suppliers</SelectItem>
                    <SelectItem value="user">Users</SelectItem>
                    <SelectItem value="document">Documents</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Activity Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activities</CardTitle>
              <CardDescription>
                View detailed logs of all user actions in the system
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No activities found</h3>
                  <p className="text-muted-foreground">
                    {searchTerm || actionFilter !== 'all' || entityFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'No activity logs available'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Entity</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Timestamp</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activities.map((activity) => (
                          <TableRow key={activity.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <div className="font-medium">{activity.user_name}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{getActionBadge(activity.action)}</TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">{activity.entity_type_display}</div>
                                {activity.entity_name && (
                                  <div className="text-sm text-muted-foreground">{activity.entity_name}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-md truncate">{activity.description}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                {format(new Date(activity.timestamp), 'MMM d, yyyy HH:mm')}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {pagination.hasMultiplePages && (
                    <div className="flex items-center justify-between mt-4">
                      <Button
                        variant="outline"
                        onClick={pagination.prevPage}
                        disabled={pagination.isFirstPage}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={pagination.nextPage}
                        disabled={pagination.isLastPage}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="stats" className="space-y-4">
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Actions Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Actions Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.by_action).map(([action, count]) => (
                      <div key={action} className="flex items-center justify-between">
                        <span className="text-sm font-medium">{action}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-muted rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${Math.min(100, (count / stats.total_activities) * 100)}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Entities Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Entities Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.by_entity).map(([entity, count]) => (
                      <div key={entity} className="flex items-center justify-between">
                        <span className="text-sm font-medium">{entity}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-muted rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${Math.min(100, (count / stats.total_activities) * 100)}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Users */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Most Active Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.top_users.map((user, index) => (
                      <div key={user.email} className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-lg">{user.activity_count}</div>
                          <div className="text-xs text-muted-foreground">activities</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
